import { Tooltip } from "antd";
import {
    Briefcase,
    Chart,
    EmojiHappy,
    Heart,
    InfoCircle,
    LampOn,
    MessageNotif,
    Messages,
    People,
    Profile2User,
    Share,
    Star1,
    Timer1,
} from "iconsax-react";
import {
    formatCount,
    formatNps,
    formatPercent,
} from "../../CustomerClub/Components/UserManagement/helpers";
import { kpiInsightCopy } from "./kpiInsights";

function percentDisplay(value, locale) {
    if (value == null || value === "") return "—";
    return `${formatPercent(value, locale)}٪`;
}

function hasScore(value) {
    return value != null && Number.isFinite(Number(value));
}

function toneForNps(score) {
    if (!hasScore(score)) return "slate";
    if (Number(score) < 0) return "rose";
    if (Number(score) < 30) return "amber";
    return "teal";
}

const TONE_COLORS = {
    teal: "#0f766e",
    amber: "#d97706",
    violet: "#7c3aed",
    rose: "#e11d48",
    coral: "#c2410c",
    gold: "#ca8a04",
    cyan: "#0891b2",
    lime: "#65a30d",
    slate: "#78716c",
};

const TONE_SURFACES = {
    teal: { bg: "#f0fdfa", border: "#99f6e4", text: "#134e4a", muted: "#0f766e" },
    amber: { bg: "#fffbeb", border: "#fde68a", text: "#78350f", muted: "#b45309" },
    violet: { bg: "#f5f3ff", border: "#ddd6fe", text: "#4c1d95", muted: "#6d28d9" },
    rose: { bg: "#fff1f2", border: "#fecdd3", text: "#881337", muted: "#be123c" },
    coral: { bg: "#fff7ed", border: "#fed7aa", text: "#7c2d12", muted: "#c2410c" },
    gold: { bg: "#fefce8", border: "#fde047", text: "#713f12", muted: "#a16207" },
    cyan: { bg: "#ecfeff", border: "#a5f3fc", text: "#164e63", muted: "#0e7490" },
    lime: { bg: "#f7fee7", border: "#d9f99d", text: "#365314", muted: "#4d7c0f" },
    slate: { bg: "#fafaf9", border: "#e7e5e4", text: "#44403c", muted: "#57534e" },
};

function KpiInsightBody({ title, how, analysis, calcLabel, statusLabel }) {
    return (
        <div className="kpi-insight-body">
            <p className="kpi-insight-title">{title}</p>
            <p className="kpi-insight-kicker">{calcLabel}</p>
            <p className="kpi-insight-copy">{how}</p>
            {analysis ? (
                <>
                    <p className="kpi-insight-kicker">{statusLabel}</p>
                    <p className="kpi-insight-copy">{analysis}</p>
                </>
            ) : null}
        </div>
    );
}

function Gauge({ value, min = 0, max = 100, color, display, label }) {
    const numeric = Number(value);
    const hasValue = value != null && Number.isFinite(numeric);
    const span = max - min || 1;
    const pct = hasValue
        ? Math.max(0, Math.min(100, ((numeric - min) / span) * 100))
        : 0;
    return (
        <div
            className="kpi-gauge"
            style={{
                background: `conic-gradient(${color} ${pct}%, #ece7e1 0)`,
            }}
            role="img"
            aria-label={label}
        >
            <span className="tabular-nums">{display}</span>
        </div>
    );
}

function KpiTile({
    tone,
    icon: Icon,
    title,
    value,
    hint,
    loading,
    score,
    min,
    max,
    showGauge,
    how,
    analysis,
    calcLabel,
    statusLabel,
    locale,
}) {
    const color = TONE_COLORS[tone] || TONE_COLORS.slate;
    const surface = TONE_SURFACES[tone] || TONE_SURFACES.slate;
    const dir = locale?.startsWith("fa") ? "rtl" : "ltr";
    const tile = (
        <article
            className={`kpi-tile kpi-tile--${tone}${
                how ? " kpi-tile--explainable" : ""
            }`}
            tabIndex={how ? 0 : undefined}
            aria-label={
                how
                    ? `${title}. ${calcLabel}: ${how}${
                          analysis ? `. ${statusLabel}: ${analysis}` : ""
                      }`
                    : undefined
            }
        >
            <div className="kpi-tile-top">
                <span className="kpi-tile-icon" aria-hidden="true">
                    <Icon size={16} variant="Bold" />
                </span>
                <span className="kpi-tile-label">{title}</span>
            </div>
            {showGauge ? (
                <Gauge
                    value={score}
                    min={min}
                    max={max}
                    color={color}
                    display={loading ? "…" : value}
                    label={title}
                />
            ) : (
                <div className="kpi-tile-value tabular-nums">
                    {loading ? "…" : value}
                </div>
            )}
            {hint ? <p className="kpi-tile-hint">{hint}</p> : null}
        </article>
    );

    if (!how) return tile;

    return (
        <Tooltip
            title={
                <KpiInsightBody
                    title={title}
                    how={how}
                    analysis={analysis}
                    calcLabel={calcLabel}
                    statusLabel={statusLabel}
                />
            }
            trigger={["hover", "click", "focus"]}
            placement="bottom"
            mouseEnterDelay={0.12}
            mouseLeaveDelay={0.2}
            overlayClassName={`kpi-insight kpi-insight--${tone}`}
            overlayStyle={{
                "--kpi-insight-bg": surface.bg,
                "--kpi-insight-border": surface.border,
                "--kpi-insight-text": surface.text,
                "--kpi-insight-muted": surface.muted,
                "--kpi-insight-accent": color,
                maxWidth: 300,
                direction: dir,
            }}
            overlayInnerStyle={{
                background: surface.bg,
                color: surface.text,
                border: `1px solid ${surface.border}`,
                borderTop: `3px solid ${color}`,
                maxWidth: 300,
                padding: 12,
                borderRadius: 10,
                boxShadow: "0 8px 24px rgba(28, 25, 23, 0.08)",
                fontSize: 12,
                lineHeight: 1.6,
                textAlign: "start",
                whiteSpace: "normal",
            }}
            getPopupContainer={() => document.body}
        >
            {tile}
        </Tooltip>
    );
}

export default function OverviewKpiStrip({
    cx,
    locale,
    t,
    cxLoading,
    messagesLoading,
    participation,
}) {
    const nps = cx?.nps || {};
    const enps = cx?.enps || {};
    const csat = cx?.csat || {};
    const ces = cx?.ces || {};
    const cxIndex = cx?.cx_index || {};
    const exIndex = cx?.ex_index || {};
    const loyalty = cx?.loyalty || {};
    const engagement = cx?.engagement || {};

    const experience = [
        {
            key: "csat",
            icon: EmojiHappy,
            tone: "teal",
            title: t("Organizational.CustomerClub.Members.Report.Csat"),
            value: percentDisplay(csat.score, locale),
            score: csat.score,
            min: 0,
            max: 100,
            showGauge: true,
            hint:
                csat.average == null
                    ? t("Organizational.CustomerClub.Members.Report.Sample", {
                          count: formatCount(csat.sample, locale),
                      })
                    : t(
                          "Organizational.CustomerClub.Members.Report.Csat.Average",
                          { value: formatPercent(csat.average, locale) }
                      ),
            sample: csat.sample,
            show: true,
        },
        {
            key: "ces",
            icon: Timer1,
            tone: "amber",
            title: t("Organizational.CustomerClub.Members.Report.Ces"),
            value: percentDisplay(ces.score, locale),
            score: ces.score,
            min: 0,
            max: 100,
            showGauge: true,
            hint:
                ces.average == null
                    ? t("Organizational.CustomerClub.Members.Report.Sample", {
                          count: formatCount(ces.sample, locale),
                      })
                    : t(
                          "Organizational.CustomerClub.Members.Report.Csat.Average",
                          { value: formatPercent(ces.average, locale) }
                      ),
            sample: ces.sample,
            show: true,
        },
        {
            key: "nps",
            icon: Chart,
            tone: toneForNps(nps.score),
            title: t("Organizational.CustomerClub.Members.Report.Nps"),
            value: formatNps(nps.score, locale),
            score: nps.score,
            min: -100,
            max: 100,
            showGauge: true,
            hint: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(nps.sample, locale),
            }),
            sample: nps.sample,
            show: hasScore(nps.score),
        },
        {
            key: "enps",
            icon: Briefcase,
            tone: toneForNps(enps.score),
            title: t("Organizational.CustomerClub.Members.Report.Enps"),
            value: formatNps(enps.score, locale),
            score: enps.score,
            min: -100,
            max: 100,
            showGauge: true,
            hint: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(enps.sample, locale),
            }),
            sample: enps.sample,
            show: hasScore(enps.score),
        },
        {
            key: "cx",
            icon: Star1,
            tone: "violet",
            title: t("Organizational.CustomerClub.Members.Report.CxIndex"),
            value: percentDisplay(cxIndex.score, locale),
            score: cxIndex.score,
            min: 0,
            max: 100,
            showGauge: true,
            hint: t("Organizational.CustomerClub.Members.Report.Sample", {
                count: formatCount(cxIndex.sample, locale),
            }),
            sample: cxIndex.sample,
            show: hasScore(cxIndex.score),
        },
        {
            key: "ex",
            icon: People,
            tone: "cyan",
            title: t("Organizational.CustomerClub.Members.Report.ExIndex"),
            value: percentDisplay(exIndex.score, locale),
            score: exIndex.score,
            min: 0,
            max: 100,
            showGauge: true,
            sample: exIndex.sample,
            show: hasScore(exIndex.score),
        },
        {
            key: "loyalty",
            icon: Heart,
            tone: "rose",
            title: t("Organizational.CustomerClub.Members.Report.Loyalty"),
            value: percentDisplay(loyalty.score, locale),
            score: loyalty.score,
            min: 0,
            max: 100,
            showGauge: true,
            sample: loyalty.advocates,
            show: hasScore(loyalty.score),
        },
        {
            key: "engagement",
            icon: Profile2User,
            tone: "lime",
            title: t("Organizational.CustomerClub.Members.Report.Engagement"),
            value: percentDisplay(engagement.score, locale),
            score: engagement.score,
            min: 0,
            max: 100,
            showGauge: true,
            sample: engagement.sample,
            show: hasScore(engagement.score),
        },
    ]
        .filter((item) => item.show)
        .map((item) => {
            const copy = kpiInsightCopy(
                t,
                item.key,
                cxLoading ? null : item.score,
                {
                    value: item.value,
                    sample: formatCount(item.sample, locale),
                }
            );
            return { ...item, ...copy };
        });

    const participationTiles = [
        {
            key: "all",
            icon: Messages,
            tone: "slate",
            title: t("all.messages"),
            value: formatCount(participation.total, locale),
        },
        {
            key: "attention",
            icon: MessageNotif,
            tone: participation.attention ? "coral" : "slate",
            title: t("attention.messages"),
            value: formatCount(participation.attention, locale),
            hint: t("attention.messages.hint"),
        },
        {
            key: "ideas",
            icon: LampOn,
            tone: "gold",
            title: t("received.ideas"),
            value: formatCount(participation.ideas, locale),
        },
        {
            key: "referrals",
            icon: Share,
            tone: "violet",
            title: t("referral.count"),
            value: formatCount(participation.referrals, locale),
        },
    ];

    return (
        <div className="kpi-band">
            <section className="kpi-group" aria-labelledby="report-cx-title">
                <div className="kpi-group-head">
                    <h2 id="report-cx-title" className="report-header">
                        {t(
                            "Organizational.CustomerClub.Members.Report.Cx.Title"
                        )}
                    </h2>
                    <Tooltip
                        title={t("reports.cx.subtitle")}
                        trigger={["hover", "focus"]}
                        placement="bottom"
                        overlayInnerStyle={{
                            maxWidth: 320,
                            fontSize: 13,
                            lineHeight: 1.6,
                            textAlign: "start",
                        }}
                    >
                        <button
                            type="button"
                            className="report-cx-hint"
                            aria-label={t("reports.cx.subtitle")}
                        >
                            <InfoCircle size={16} aria-hidden="true" />
                        </button>
                    </Tooltip>
                </div>
                <div
                    className="kpi-grid"
                    style={{ "--kpi-cols": experience.length }}
                >
                    {experience.map((item) => (
                        <KpiTile
                            key={item.key}
                            {...item}
                            loading={cxLoading}
                            locale={locale}
                            calcLabel={t("reports.kpi.calc")}
                            statusLabel={t("reports.kpi.status")}
                        />
                    ))}
                </div>
            </section>
            <section
                className="kpi-group"
                aria-labelledby="report-participation-title"
            >
                <div className="kpi-group-head">
                    <h2
                        id="report-participation-title"
                        className="report-header"
                    >
                        {t("reports.section.participation")}
                    </h2>
                </div>
                <div
                    className="kpi-grid"
                    style={{ "--kpi-cols": participationTiles.length }}
                >
                    {participationTiles.map((item) => (
                        <KpiTile
                            key={item.key}
                            {...item}
                            loading={
                                item.key === "ideas" || item.key === "referrals"
                                    ? cxLoading
                                    : messagesLoading
                            }
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}
